import { pointsFor } from '../model/types.js';
import { dpFromFraction, expectedFromDifference } from '../ratings/fide-tables.js';
import type { TbEntrant, TbRound, TiebreakContext } from './model.js';

/**
 * FIDE C.07 (2026) tiebreak computations. The handling of unplayed rounds
 * (Article 16, with the 2026 caps) and of cut modifiers follows the
 * reference implementation Gacrux (MIT, copyright FIDE, www.gacrux.no).
 *
 * Units: scores are tenths of a point; products (SB) are hundredths.
 * Every value is "higher is better" unless the descriptor carries /R.
 */

export interface Descriptor {
  /** Canonical text, e.g. "BH/C1". */
  readonly text: string;
  readonly name: string;
  readonly cutLow: number;
  readonly cutHigh: number;
  /** Koya limit adjustment in tenths (/L±n, n in half points... stored in tenths). */
  readonly limit: number;
  /** /P: forfeits count as played against the scheduled opponent. */
  readonly forfeitsPlayed: boolean;
  /** /F: Fore Buchholz instead of Buchholz. */
  readonly fore: boolean;
  /** /R: reverse order. */
  readonly reverse: boolean;
}

interface Contribution {
  readonly vur: boolean;
  /** The opponent's (or dummy's) score used for ordering cuts. */
  readonly score: number;
  readonly value: number;
}

function entrant(ctx: TiebreakContext, id: number): TbEntrant | undefined {
  return ctx.entrants.get(id);
}

/** Is the round treated as a game against its scheduled opponent? */
function asPlayed(r: TbRound, ctx: TiebreakContext, d: Descriptor): boolean {
  if (r.opponent === null) return false;
  if (r.played) return true;
  // 15.2 pre-determined pairings: forfeits are regular games; /P likewise.
  return ctx.predetermined || d.forfeitsPlayed;
}

/** Adjusted score of an opponent, or Fore-adjusted when computing FB. */
function oppScore(ctx: TiebreakContext, id: number, fore: boolean): number {
  const e = entrant(ctx, id);
  if (e === undefined) return 0;
  return fore ? foreAdjusted(ctx, e) : e.adjusted;
}

/**
 * Fore Buchholz (8.3): scores as if every paired game of the final round had
 * been drawn.
 */
function foreAdjusted(ctx: TiebreakContext, e: TbEntrant): number {
  const fore = foreScore(ctx, e);
  return fore ?? e.adjusted;
}

/**
 * The score with the final round counted as a drawn game, or null when the
 * participant was not paired in the final round (or it is not yet played).
 * A paired final round makes every earlier unplayed round non-voluntary
 * (16.2.3 rather than 16.2.5), so the actual points are used for them, as
 * Gacrux does.
 */
function foreScore(ctx: TiebreakContext, e: TbEntrant): number | null {
  const last = e.rounds[ctx.totalRounds - 1];
  if (last === undefined || ctx.rounds < ctx.totalRounds || last.opponent === null) return null;
  return e.score - last.points + ctx.pointSystem.draw;
}

/** Buchholz / Sonneborn-Berger contributions per round (Article 16). */
function bhContributions(
  ctx: TiebreakContext,
  me: TbEntrant,
  d: Descriptor,
  sb: boolean,
): Contribution[] {
  const ps = ctx.pointSystem;
  const own = d.fore ? (foreScore(ctx, me) ?? me.score) : me.score;
  const out: Contribution[] = [];
  for (const r of me.rounds) {
    let score: number;
    let vur = r.vur;
    const isFinalFore = d.fore && r.round === ctx.totalRounds && r.opponent !== null;
    if ((asPlayed(r, ctx, d) || isFinalFore) && r.opponent !== null) {
      vur = false;
      score = oppScore(ctx, r.opponent, d.fore);
    } else if (!ctx.predetermined) {
      // 16.4: a dummy opponent with the participant's own score, capped.
      score = own;
      if (r.opponent !== null) score = Math.min(score, oppScore(ctx, r.opponent, d.fore));
      else score = Math.min(score, ps.draw * ctx.rounds);
    } else {
      continue;
    }
    const pts = isFinalFore ? ps.draw : r.points;
    out.push({ vur, score, value: sb ? score * pts : score });
  }
  return out;
}

/** Apply Cut-n / Median-n with the 16.5 exception for voluntary unplayed rounds. */
function applyCuts(parts: Contribution[], d: Descriptor, rounds: number): Contribution[] {
  let low = Math.min(d.cutLow, rounds);
  let high = Math.min(d.cutHigh, rounds - low);
  const list = [...parts];
  while (low > 0 && list.length > 0) {
    const all = [...list].sort((a, b) => a.score - b.score || a.value - b.value);
    const exp = [...list].sort(
      (a, b) => Number(b.vur) - Number(a.vur) || a.value - b.value || a.score - b.score,
    );
    const a0 = all[0] as Contribution;
    const e0 = exp[0] as Contribution;
    const victim = !e0.vur || a0.value > e0.value ? a0 : e0;
    list.splice(list.indexOf(victim), 1);
    low--;
  }
  while (high > 0 && list.length > 0) {
    const all = [...list].sort((a, b) => b.score - a.score || b.value - a.value);
    list.splice(list.indexOf(all[0] as Contribution), 1);
    high--;
  }
  return list;
}

const sum = (xs: readonly number[]): number => xs.reduce((a, b) => a + b, 0);

function playedRounds(e: TbEntrant): TbRound[] {
  return e.rounds.filter((r) => r.played && r.opponent !== null);
}

function aro(ctx: TiebreakContext, e: TbEntrant, d: Descriptor): number {
  const ratings = playedRounds(e)
    .map((r) => entrant(ctx, r.opponent as number)?.rating ?? 0)
    .filter((x) => x > 0)
    .sort((a, b) => a - b);
  const kept = ratings.slice(
    Math.min(d.cutLow, ratings.length),
    Math.max(0, ratings.length - d.cutHigh),
  );
  if (kept.length === 0) return 0;
  return Math.floor(sum(kept) / kept.length + 0.5);
}

/** Tournament performance rating (10.2): ARO + dp(fractional score over the board). */
export function tpr(ctx: TiebreakContext, e: TbEntrant): number {
  const games = playedRounds(e);
  if (games.length === 0) return 0;
  const a = aro(ctx, e, NO_CUT);
  const pts = sum(games.map((r) => r.points)) / ctx.pointSystem.win;
  return a + dpFromFraction(pts / games.length);
}

/**
 * Perfect tournament performance (10.3): the lowest rating whose expected
 * score against the opponents reaches the score over the board; a zero score
 * gives the lowest opponent rating minus 800. No 400-point cut.
 */
export function ptp(ctx: TiebreakContext, e: TbEntrant): number {
  const games = playedRounds(e);
  const opps = games
    .map((r) => entrant(ctx, r.opponent as number)?.rating ?? 0)
    .filter((x) => x > 0);
  if (opps.length === 0) return 0;
  const pts = sum(games.map((r) => r.points)) / ctx.pointSystem.win;
  if (pts <= 0) return Math.min(...opps) - 800;
  const expected = (r: number): number => sum(opps.map((o) => expectedFromDifference(r - o)));
  let lo = Math.min(...opps) - 800;
  let hi = Math.max(...opps) + 800;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (expected(mid) + 1e-9 >= pts) hi = mid;
    else lo = mid + 1;
  }
  return lo;
}

const NO_CUT: Descriptor = {
  text: '',
  name: '',
  cutLow: 0,
  cutHigh: 0,
  limit: 0,
  forfeitsPlayed: false,
  fore: false,
  reverse: false,
};

/** Compute a type B/C/D tiebreak (everything except direct encounter). */
export function computeValue(ctx: TiebreakContext, id: number, d: Descriptor): number {
  const me = entrant(ctx, id);
  if (me === undefined) return 0;
  const ps = ctx.pointSystem;
  const v = ((): number => {
    switch (d.name) {
      case 'PTS':
        return me.score;
      case 'BH':
      case 'FB':
        return sum(
          applyCuts(
            bhContributions(ctx, me, { ...d, fore: d.fore || d.name === 'FB' }, false),
            d,
            ctx.rounds,
          ).map((c) => c.value),
        );
      case 'SB':
        return sum(applyCuts(bhContributions(ctx, me, d, true), d, ctx.rounds).map((c) => c.value));
      case 'AOB': {
        const opps = playedRounds(me).map((r) => r.opponent as number);
        if (opps.length === 0) return 0;
        const bh = opps.map((o) =>
          sum(
            bhContributions(
              ctx,
              entrant(ctx, o) as TbEntrant,
              { ...NO_CUT, fore: d.fore },
              false,
            ).map((c) => c.value),
          ),
        );
        // Decimals grow with the rounds so every distinct average stays
        // distinct (Gacrux's convention): 2 below 9 rounds, 3 below 27, 4
        // below 73, else 5; rounded half up. Values are in tenths.
        const decimals = ctx.rounds < 9 ? 2 : ctx.rounds < 27 ? 3 : ctx.rounds < 73 ? 4 : 5;
        const scale = 10 ** (decimals - 1);
        return Math.round((sum(bh) * scale) / opps.length) / scale;
      }
      case 'WIN':
        return me.rounds.filter((r) => r.points === ps.win && r.result !== '*').length;
      case 'WON':
        return me.rounds.filter((r) => r.played && r.points === ps.win).length;
      case 'BPG':
        return me.rounds.filter((r) => r.played && r.colour === 'b').length;
      case 'BWG':
        return me.rounds.filter((r) => r.played && r.colour === 'b' && r.points === ps.win).length;
      case 'REP':
        return me.rounds.filter((r) => r.played || r.points === ps.win).length;
      case 'PS': {
        let run = 0;
        const cum: number[] = [];
        for (const r of me.rounds) {
          run += r.points;
          cum.push(run);
        }
        return sum(cum.slice(Math.min(d.cutLow, cum.length)));
      }
      case 'STD': {
        // 7.7: one per round won (over the board or more than a draw without
        // playing), a half per round drawn (or a draw's worth without playing).
        let s = 0;
        for (const r of me.rounds) {
          if (r.played && r.opponent !== null) {
            const theirs = pointsFor(
              (entrant(ctx, r.opponent)?.rounds[r.round - 1]?.result ?? '0') as TbRound['result'],
              ps,
            );
            if (r.points > theirs) s += 10;
            else if (r.points === theirs) s += 5;
          } else if (r.points > ps.draw) s += 10;
          else if (r.points === ps.draw) s += 5;
        }
        return s;
      }
      case 'KS': {
        // Points scored against opponents (forfeits included) whose final
        // score reaches half the maximum possible; in round robins with an
        // odd field the rest rounds do not count towards the maximum.
        let maxGames = ctx.rounds;
        if (ctx.predetermined) {
          const n = ctx.entrants.size;
          if (n > 0 && ctx.rounds % n === 0) maxGames -= ctx.rounds / n;
          for (const e of ctx.entrants.values()) {
            if (!e.rounds.some((r) => r.opponent !== null)) maxGames -= 1;
          }
        }
        const limit = (maxGames * ps.win) / 2 + d.limit;
        let s = 0;
        for (const r of me.rounds) {
          if (r.opponent === null) continue;
          const o = entrant(ctx, r.opponent);
          if (o !== undefined && o.score >= limit) s += r.points;
        }
        return s;
      }
      case 'ARO':
        return aro(ctx, me, d);
      case 'TPR':
        return tpr(ctx, me);
      case 'PTP':
        return ptp(ctx, me);
      case 'APRO':
      case 'APPO': {
        const opps = playedRounds(me).map((r) => entrant(ctx, r.opponent as number) as TbEntrant);
        if (opps.length === 0) return 0;
        const vals = opps.map((o) => (d.name === 'APRO' ? tpr(ctx, o) : ptp(ctx, o)));
        return Math.floor(sum(vals) / vals.length + 0.5);
      }
      case 'RTNG':
        return me.rating;
      case 'TPN':
        return -me.id;
      default:
        return computeUsValue(ctx, me, d);
    }
  })();
  return d.reverse ? -v : v;
}

/**
 * US Chess tiebreaks (Rule 34E): opponents' unplayed games count as draws
 * (adjusted score); the player's own unplayed rounds count as opponents
 * with an adjusted score of zero.
 */
function usOpponentScores(ctx: TiebreakContext, me: TbEntrant): number[] {
  const ps = ctx.pointSystem;
  return me.rounds.map((r) => {
    if (!r.played || r.opponent === null) return 0;
    const o = entrant(ctx, r.opponent);
    if (o === undefined) return 0;
    return sum(o.rounds.map((x) => (x.played ? x.points : ps.draw)));
  });
}

function computeUsValue(ctx: TiebreakContext, me: TbEntrant, d: Descriptor): number {
  const ps = ctx.pointSystem;
  switch (d.name) {
    case 'SOLK':
      return sum(usOpponentScores(ctx, me));
    case 'MM': {
      // Modified median: even score drops highest and lowest; plus score
      // drops the lowest; minus score drops the highest; 9+ rounds drop two.
      const s = [...usOpponentScores(ctx, me)].sort((a, b) => a - b);
      const k = ctx.rounds >= 9 ? 2 : 1;
      const half = (ctx.rounds * ps.win) / 2;
      let lo = 0;
      let hi = 0;
      if (me.score === half) {
        lo = k;
        hi = k;
      } else if (me.score > half) lo = k;
      else hi = k;
      return sum(s.slice(lo, Math.max(lo, s.length - hi)));
    }
    case 'MED': {
      const s = [...usOpponentScores(ctx, me)].sort((a, b) => a - b);
      const k = ctx.rounds >= 9 ? 2 : 1;
      return sum(s.slice(k, Math.max(k, s.length - k)));
    }
    case 'CUM': {
      // Cumulative: running scores, minus the unplayed wins (1) and draws (1/2).
      let run = 0;
      let total = 0;
      for (const r of me.rounds) {
        run += r.points;
        total += run;
      }
      for (const r of me.rounds) {
        if (!r.played) total -= r.points;
      }
      return total;
    }
    case 'OPPCUM': {
      let total = 0;
      for (const r of me.rounds) {
        if (!r.played || r.opponent === null) continue;
        const o = entrant(ctx, r.opponent);
        if (o === undefined) continue;
        total += computeUsValue(ctx, o, { ...d, name: 'CUM' });
      }
      return total;
    }
    case 'KASH':
      // Kashdan: 4 per win, 2 per draw, 1 per loss, 0 unplayed.
      return sum(
        me.rounds.map((r) => {
          if (!r.played) return 0;
          if (r.points === ps.win) return 4;
          if (r.points === ps.draw) return 2;
          return 1;
        }),
      );
    case 'BLACKS':
      return me.rounds.filter((r) => r.played && r.colour === 'b').length;
    case 'OPERF': {
      // Opposition performance: average of opponents' (rating +400 per win, -400 per loss).
      const vals = playedRounds(me).map((r) => {
        const o = entrant(ctx, r.opponent as number);
        const rating = o?.rating ?? 0;
        if (r.points === ps.win) return rating + 400;
        if (r.points === ps.loss) return rating - 400;
        return rating;
      });
      return vals.length === 0 ? 0 : Math.round(sum(vals) / vals.length);
    }
    default:
      throw new Error(`unknown tiebreak ${d.name}`);
  }
}

/**
 * Direct encounter (Article 6) among a group of tied participants. Returns
 * a value per participant; equal values stay tied. Forfeits count only with
 * /P (or in pre-determined pairings). If not everyone met, only participants
 * who are ahead whatever the missing results are separated.
 */
export function directEncounter(
  ctx: TiebreakContext,
  group: readonly number[],
  d: Descriptor,
): Map<number, number> {
  const ps = ctx.pointSystem;
  const ids = new Set(group);
  const points = new Map<number, number>();
  const missing = new Map<number, number>();
  for (const id of group) {
    const me = entrant(ctx, id) as TbEntrant;
    let total = 0;
    let miss = 0;
    for (const other of group) {
      if (other === id) continue;
      const games = me.rounds.filter((r) => r.opponent === other && asPlayed(r, ctx, d));
      if (games.length === 0) miss++;
      else total += sum(games.map((r) => r.points)) / games.length;
    }
    points.set(id, total);
    missing.set(id, miss);
  }
  const complete = [...missing.values()].every((m) => m === 0);
  if (complete) return points;
  // Incomplete: peel off participants guaranteed to be ahead of all others.
  const out = new Map<number, number>();
  let rest = [...ids];
  let level = group.length;
  for (;;) {
    const best = rest.find((p) =>
      rest.every(
        (q) =>
          q === p ||
          (points.get(p) as number) >
            (points.get(q) as number) + (missing.get(q) as number) * ps.win,
      ),
    );
    if (best === undefined) break;
    out.set(best, level--);
    rest = rest.filter((q) => q !== best);
    if (rest.length <= 1) break;
  }
  for (const q of rest) out.set(q, 0);
  return out;
}
